import { useEffect, useMemo, useState } from "react";
import { FaPlus, FaTrash, FaShieldAlt, FaEye, FaEyeSlash, FaFileImport } from "react-icons/fa";
import { PERSONAL_VAULT_CONSTANTS } from "../_MODULES/_API";
import {
    OPTIONS_PERSONAL_VAULT_ENTRY_TYPE, OPTIONS_CODE_LANGUAGE, OPTIONS_PERSONAL_VAULT_AUTH_MODE,
    OPTIONS_PERSONAL_VAULT_FILE_PURPOSE,
} from "../../../_Catalogs.Views";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import {
    FormGrid, FormSection, FieldInput, FieldSelect, FieldTextarea, FieldCode,
    FieldRichText, FieldSwitch, FieldFile, useForm,
} from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { API_CORE } from "../../../../../_CORE/API/ApiCORE";
import { REACT_HOT_TOAST_ERROR } from "../../../../../_CORE/HELPERS/reactHotToast.helper";
import { extractRecoveryCodesFromText, maskRecoveryCode, readRecoveryCodesFile } from "../_UTILS/recoveryCodes.util";
import {
    CredentialSpecializedSections, onEmptyCredentialTechnical, onSplitCredentialTechnicalExtra,
    onMergeCredentialTechnicalExtra,
} from "../../../../_UI/CredentialSpecializedSections";

const EMPTY_EXTRA = { label: "", value: "", isSecret: false };

const EMPTY_VALUES = {
    parentId: "", title: "", type: "ACCOUNT", environment: "", host: "", port: "", url: "",
    email: "", username: "", accountId: "", phone: "", secret: "", passwordHint: "", securityPin: "",
    token: "", connectionString: "", sshKey: "", databaseName: "",
    authMode: "NONE", authIssuer: "", authAccount: "", authDevice: "", authSecret: "",
    recoveryEmail: "", recoveryPhone: "", securityNotes: "",
    passwordChangedAt: "", passwordExpiresAt: "",
    notes: "", command: "", commandLanguage: "bash", isFavorite: false,
};

const SECRET_FIELDS = [
    "secret", "securityPin", "token", "connectionString", "sshKey", "authSecret", "securityNotes",
];

export const PersonalVaultEntryFormModal = ({
    isOpen, groupId, accessPin = null, entry = null, revealed = null, parentOptions = [], defaultParentId = null, isStacked = false, onClose, onSaved,
}) => {

    const [useExtra, setExtra] = useState([]);
    const [useTechnical, setTechnical] = useState(onEmptyCredentialTechnical);
    const [useRecoveryCodesText, setRecoveryCodesText] = useState("");
    const [useRecoveryFileName, setRecoveryFileName] = useState("");
    const [useRecoveryImportError, setRecoveryImportError] = useState("");
    const [useRecoveryPreviewVisible, setRecoveryPreviewVisible] = useState({});
    const [useRecoveryReplace, setRecoveryReplace] = useState(false);
    const [useRecoveryFiles, setRecoveryFiles] = useState([]);
    const [useRecoveryPurpose, setRecoveryPurpose] = useState("RECOVERY_KIT");
    const [useSavingExtras, setSavingExtras] = useState(false);
    const IS_EDIT = !!entry?.id;

    const RECOVERY_CODES = useMemo(
        () => extractRecoveryCodesFromText(useRecoveryCodesText),
        [useRecoveryCodesText],
    );

    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 400,
    });

    const FORM = useForm({
        initialValues: EMPTY_VALUES,
        schema: {
            title: { required: true, minLength: 2, maxLength: 160 },
            environment: { maxLength: 120 },
            host: { maxLength: 255 },
            port: { port: true },
            url: { url: true, maxLength: 255 },
            email: { email: true, maxLength: 254 },
            username: { maxLength: 255 },
            accountId: { maxLength: 255 },
            phone: { maxLength: 64 },
            recoveryEmail: { email: true, maxLength: 254 },
            recoveryPhone: { maxLength: 64 },
            passwordChangedAt: { date: true },
            passwordExpiresAt: {
                date: true,
                custom: (value, values) => (
                    value && values.passwordChangedAt && value < values.passwordChangedAt
                        ? "La fecha de rotación no puede ser anterior al último cambio"
                        : null
                ),
            },
            commandLanguage: { maxLength: 64 },
        },

        onSubmit: async (values) => {
            const PAYLOAD = {
                parentId: values.parentId || null,
                accessPin: accessPin ?? undefined,
                title: values.title.trim(),
                extra: onMergeCredentialTechnicalExtra(
                    useExtra.filter((field) => !!field.label.trim()),
                    useTechnical,
                ),
                type: values.type,
                environment: values.environment || "",
                host: values.host || "",
                port: values.port || "",
                url: values.url || "",
                email: values.email || "",
                username: values.username || "",
                accountId: values.accountId || "",
                phone: values.phone || "",
                databaseName: values.databaseName || "",
                authMode: values.authMode || "NONE",
                authIssuer: values.authIssuer || "",
                authAccount: values.authAccount || "",
                authDevice: values.authDevice || "",
                recoveryEmail: values.recoveryEmail || "",
                recoveryPhone: values.recoveryPhone || "",
                passwordChangedAt: values.passwordChangedAt || null,
                passwordExpiresAt: values.passwordExpiresAt || null,
                notes: values.notes || "",
                command: values.command || "",
                commandLanguage: values.commandLanguage,
                isFavorite: !!values.isFavorite,
            };

            if (!IS_EDIT) {
                PAYLOAD.groupId = groupId;
            }

            PAYLOAD.passwordHint = values.passwordHint ?? "";

            SECRET_FIELDS.forEach((field) => {
                if (values[field]) {
                    PAYLOAD[field] = values[field];
                    return;
                }

                // Si el usuario reveló el registro y borró un secreto, ese vacío es intencional.
                if (IS_EDIT && revealed) PAYLOAD[field] = "";
            });

            const RESPONSE = IS_EDIT
                ? await actionMutate({
                    method: "PATCH", endpoint: PERSONAL_VAULT_CONSTANTS.ENTRY_UPDATE,
                    successMessage: "Acceso actualizado", id: entry.id, data: PAYLOAD,
                })
                : await actionMutate({
                    method: "POST", endpoint: PERSONAL_VAULT_CONSTANTS.ENTRY_CREATE,
                    successMessage: "Acceso guardado", data: PAYLOAD,
                });

            if (!RESPONSE.success) {
                FORM.applyApiError(RESPONSE);
                return;
            }

            let saved = RESPONSE.data;
            const entryId = saved?.id || entry?.id;

            setSavingExtras(true);
            try {
                if (entryId && RECOVERY_CODES.length > 0) {
                    const url = API_CORE.ENDPOINTS[PERSONAL_VAULT_CONSTANTS.RECOVERY_CODES_IMPORT].replace(":id", entryId);
                    const recoveryResponse = await API_CORE.SERVICES.POST_RESOURCE(url, {
                        codes: RECOVERY_CODES,
                        replace: IS_EDIT ? useRecoveryReplace : false,
                        accessPin: accessPin ?? undefined,
                    });

                    if (recoveryResponse?.success === false) {
                        REACT_HOT_TOAST_ERROR(recoveryResponse?.message || "El acceso se guardó, pero no se pudieron guardar los códigos de recuperación.");
                    } else {
                        const recoveryCodes = Array.isArray(recoveryResponse?.data) ? recoveryResponse.data : [];
                        saved = { ...saved, recoveryCodes };
                    }
                }

                if (entryId && useRecoveryFiles.length > 0) {
                    const formData = new FormData();
                    formData.append("groupId", groupId);
                    formData.append("entryId", entryId);
                    formData.append("title", "Respaldo de acceso");
                    formData.append("purpose", useRecoveryPurpose);
                    if (accessPin) formData.append("accessPin", accessPin);
                    useRecoveryFiles.forEach((file) => formData.append("files[]", file));

                    const fileResponse = await API_CORE.SERVICES.POST_RESOURCE(
                        API_CORE.ENDPOINTS[PERSONAL_VAULT_CONSTANTS.FILE_UPLOAD],
                        formData,
                    );

                    if (fileResponse?.success === false) {
                        REACT_HOT_TOAST_ERROR(fileResponse?.message || "El acceso se guardó, pero no se pudieron subir los archivos de recuperación.");
                    } else {
                        const uploadedFiles = Array.isArray(fileResponse?.data) ? fileResponse.data : [];
                        saved = { ...saved, files: [...(entry?.files ?? []), ...uploadedFiles] };
                    }
                }

            } catch (error) {
                REACT_HOT_TOAST_ERROR(error?.message || "El acceso se guardó, pero no se pudieron completar los datos adicionales.");
            } finally {
                setSavingExtras(false);
            }

            onSaved?.(saved, IS_EDIT);
            onClose?.();
        },
    });

    useEffect(() => {
        if (!isOpen) return;

        setRecoveryCodesText("");
        setRecoveryFileName("");
        setRecoveryImportError("");
        setRecoveryPreviewVisible({});
        setRecoveryReplace(false);
        setRecoveryFiles([]);
        setRecoveryPurpose("RECOVERY_KIT");
        if (IS_EDIT) {
            const SPLIT_EXTRA = onSplitCredentialTechnicalExtra(revealed?.extra ?? entry.extra ?? []);
            setExtra(SPLIT_EXTRA.custom);
            setTechnical(SPLIT_EXTRA.technical);
        } else {
            setExtra([]);
            setTechnical(onEmptyCredentialTechnical());
        }

        FORM.reset(IS_EDIT ? {
            parentId: entry.parentId ?? "",
            title: entry.title ?? "",
            type: entry.type ?? "ACCOUNT",
            environment: entry.environment ?? "",
            host: entry.host ?? "",
            port: entry.port ?? "",
            url: entry.url ?? "",
            email: entry.email ?? "",
            username: entry.username ?? "",
            accountId: entry.accountId ?? "",
            phone: entry.phone ?? "",
            databaseName: entry.databaseName ?? "",
            authMode: entry.authMode ?? "NONE",
            authIssuer: entry.authIssuer ?? "",
            authAccount: entry.authAccount ?? "",
            authDevice: entry.authDevice ?? "",
            recoveryEmail: entry.recoveryEmail ?? "",
            recoveryPhone: entry.recoveryPhone ?? "",
            passwordChangedAt: entry.passwordChangedAt ?? "",
            passwordExpiresAt: entry.passwordExpiresAt ?? "",
            notes: entry.notes ?? "",
            command: entry.command ?? "",
            commandLanguage: entry.commandLanguage ?? "bash",
            isFavorite: !!entry.isFavorite,
            secret: revealed?.secret ?? "",
            passwordHint: revealed?.passwordHint ?? entry.passwordHint ?? "",
            securityPin: revealed?.securityPin ?? "",
            token: revealed?.token ?? "",
            connectionString: revealed?.connectionString ?? "",
            sshKey: revealed?.sshKey ?? "",
            authSecret: revealed?.authSecret ?? "",
            securityNotes: revealed?.securityNotes ?? "",
        } : { ...EMPTY_VALUES, parentId: defaultParentId ?? "" });

    }, [isOpen, entry, revealed]); // eslint-disable-line react-hooks/exhaustive-deps

    const onChangeExtra = (index, field, value) => {
        setExtra((prev) => prev.map((row, position) => (
            position === index ? { ...row, [field]: value } : row
        )));
    };

    const onChangeTechnical = (field, value) => {
        setTechnical((prev) => ({ ...prev, [field]: value }));
    };

    const onAddExtra = () => setExtra((prev) => [...prev, { ...EMPTY_EXTRA }]);
    const onRemoveExtra = (index) => setExtra((prev) => prev.filter((row, position) => position !== index));

    const onImportRecoveryFile = async (files) => {
        const FILE = files?.[0];
        if (!FILE) return;

        setRecoveryImportError("");

        try {
            const CODES = await readRecoveryCodesFile(FILE);
            if (CODES.length === 0) {
                setRecoveryFileName(FILE.name);
                setRecoveryImportError("No encontramos códigos válidos en el archivo.");
                return;
            }

            setRecoveryCodesText(CODES.join("\n"));
            setRecoveryFileName(FILE.name);
            setRecoveryPreviewVisible({});
        } catch (error) {
            setRecoveryFileName(FILE.name || "");
            setRecoveryImportError(error?.message || "No se pudo leer el archivo.");
        }
    };

    const onChangeRecoveryCodesText = (event) => {
        setRecoveryCodesText(event.target.value);
        setRecoveryFileName("");
        setRecoveryImportError("");
        setRecoveryPreviewVisible({});
    };

    const onToggleRecoveryPreview = (index) => {
        setRecoveryPreviewVisible((prev) => ({ ...prev, [index]: !prev[index] }));
    };

    const HAS_CHILDREN = (entry?.children ?? []).length > 0;
    const PARENT_OPTIONS = [
        { value: "", label: "No depende de nada, va al nivel principal" },
        ...(HAS_CHILDREN ? [] : parentOptions
            .filter((option) => option.value !== entry?.id)
            .map((option) => ({ ...option }))),
    ];

    const SECRET_PLACEHOLDER = IS_EDIT && !revealed ? "Sin cambios" : "";

    return (
        <AppModal
            isOpen={isOpen}
            onClose={onClose}
            className={isStacked ? "LStudio__app_nested_vault_child_form" : ""}
            isDirty={FORM.isDirty || !!useRecoveryCodesText.trim() || useRecoveryFiles.length > 0}
            size="xl"
            title={IS_EDIT ? "Editar acceso" : "Nuevo acceso"}
            description={
                IS_EDIT && !revealed
                    ? "Los secretos reales permanecen protegidos. La pista / palabra secreta sí se muestra completa para que puedas verificarla sin revelar el resto."
                    : "Contraseñas, pista, PIN, secretos 2FA, tokens y notas de seguridad se guardan cifrados. La pista se muestra completa dentro de NOTEBOOK."
            }
            footer={
                <>
                    <AppButton variant="ghost" tone="neutral" onClick={onClose}>Cancelar</AppButton>
                    <AppButton onClick={FORM.onSubmit} isLoading={useActionApiIsLoading || useSavingExtras}>
                        {IS_EDIT ? "Guardar cambios" : "Guardar acceso"}
                    </AppButton>
                </>
            } >

            <div className="LStudio__app_priority_toggle">
                <FieldSwitch id="personal_vault_entry_isFavorite" label="Destacado" text="Mostrar este acceso primero"
                    isChecked={!!FORM.values.isFavorite}
                    onChange={(event) => FORM.setValue("isFavorite", event.target.checked)} />
            </div>

            <div className="LStudio__app_vault_form_guide">
                <FaShieldAlt />
                <div>
                    <strong>Guía rápida.</strong> Guardá sólo lo necesario. La contraseña, los secretos y los códigos de
                    recuperación se cifran en el backend. El archivo TXT/CSV de códigos se lee localmente y no se sube.
                </div>
            </div>

            {FORM.serverError && (
                <div className="LStudio__app_vault_form_error_summary" role="alert" aria-live="assertive">
                    <strong>No se pudo completar la operación.</strong>
                    <span>{FORM.serverError}</span>
                    {FORM.requestId && <small>Referencia técnica: {FORM.requestId}</small>}
                </div>
            )}

            <FormSection title="Identificación" description="Qué cuenta es y cómo la reconocés.">
                <FormGrid columns={3}>
                    <FieldInput {...FORM.field("title")} label="Título" placeholder="Ej: Cuenta principal de Google" />
                    <FieldSelect {...FORM.field("type")} label="Tipo" options={OPTIONS_PERSONAL_VAULT_ENTRY_TYPE} />
                    <FieldInput {...FORM.field("environment")} label="Entorno" placeholder="Ej: Personal" />

                    <FieldSelect {...FORM.field("parentId")} label="Depende de" options={PARENT_OPTIONS}
                        hint={HAS_CHILDREN ? "Este acceso ya es padre; no puede convertirse en hijo." : "Vacío = acceso principal. Elegí un padre para crear una única relación padre → hija."} span={3} />

                    <FieldInput {...FORM.field("accountId")} label="ID / número de cliente" placeholder="Ej: 00498271" />
                    <FieldInput {...FORM.field("phone")} label="Teléfono asociado" placeholder="Ej: +54 9 11 ..." />
                    <FieldInput {...FORM.field("securityPin")} type="pin" label="PIN de la cuenta"
                        placeholder={SECRET_PLACEHOLDER} hint="Para PIN bancario, PIN de soporte u otro código recuperable." />

                    <FieldInput {...FORM.field("passwordChangedAt")} type="date" label="Último cambio de contraseña" />
                    <FieldInput {...FORM.field("passwordExpiresAt")} type="date" label="Rotar / expira el" />
                </FormGrid>
            </FormSection>

            <CredentialSpecializedSections
                idPrefix="personal_vault_credential"
                type={FORM.values.type}
                form={FORM}
                technical={useTechnical}
                onTechnicalChange={onChangeTechnical}
                secretPlaceholder={SECRET_PLACEHOLDER}
                includeUrl
                renderSshKey
            />

            <FormSection title="Autenticación en 2 pasos" columns={1}
                description="Registrá cómo recuperás el segundo factor si perdés el teléfono o el autenticador.">
                <FormGrid columns={3}>
                    <FieldSelect {...FORM.field("authMode")} label="Método 2FA"
                        options={OPTIONS_PERSONAL_VAULT_AUTH_MODE}
                        hint="Google Authenticator, Microsoft Authenticator, SMS, Passkey, llave física, etc." />
                    <FieldInput {...FORM.field("authIssuer")} label="Servicio / emisor" placeholder="Ej: Google, GitHub, Microsoft" />
                    <FieldInput {...FORM.field("authAccount")} label="Cuenta en el autenticador" placeholder="Ej: cuenta@gmail.com" />
                    <FieldInput {...FORM.field("authDevice")} label="Dispositivo o llave" placeholder="Ej: Pixel 9 / YubiKey 5 NFC" />
                    <FieldInput {...FORM.field("authSecret")} type="pin" label="Semilla / secreto TOTP"
                        placeholder={SECRET_PLACEHOLDER}
                        hint="Opcional. Guardalo sólo si necesitás reconstruir el autenticador; se cifra." />
                    <FieldInput {...FORM.field("recoveryEmail")} label="Email de recuperación" placeholder="Ej: respaldo@gmail.com" />
                    <FieldInput {...FORM.field("recoveryPhone")} label="Teléfono de recuperación" placeholder="Ej: +54 9 11 ..." />
                    <FieldTextarea {...FORM.field("securityNotes")} label="Notas de seguridad cifradas" rows={4}
                        placeholder={IS_EDIT && !revealed ? "Sin cambios" : "Ej: procedimiento de recuperación, ubicación de llave física, instrucciones privadas..."}
                        hint="Este campo sí se cifra. Para notas comunes usá la sección final." span={3} />
                </FormGrid>

                <div className="LStudio__app_vault_recovery_create_import">
                    <div className="LStudio__app_vault_recovery_create_head">
                        <div>
                            <strong>Códigos de recuperación de un solo uso</strong>
                            <span>
                                {IS_EDIT
                                    ? "Importá o pegá códigos sólo si querés agregar un conjunto nuevo o reemplazar el actual."
                                    : "Importá el archivo del proveedor o pegalos manualmente. Se guardan cifrados con el acceso."}
                            </span>
                        </div>
                        <span className="LStudio__app_vault_recovery_create_count">
                            {RECOVERY_CODES.length > 0
                                ? `${RECOVERY_CODES.length} detectado${RECOVERY_CODES.length === 1 ? "" : "s"}`
                                : `${entry?.recoveryCodes?.length ?? 0} guardado${(entry?.recoveryCodes?.length ?? 0) === 1 ? "" : "s"}`}
                        </span>
                    </div>

                    <FormGrid columns={2}>
                        <FieldFile id="personal_vault_recovery_codes_file" label="Importar TXT / CSV"
                            files={[]} onChange={onImportRecoveryFile} span={1} maxFiles={1} maxSize={1048576}
                            accept={{ "text/plain": [".txt"], "text/csv": [".csv"] }}
                            dropText="Arrastrá el archivo de códigos o hacé clic para elegir"
                            hint="Se procesa sólo en tu navegador; el archivo original no se envía." />
                        <FieldTextarea id="personal_vault_recovery_codes_text" label="O pegarlos manualmente" rows={6} span={1}
                            value={useRecoveryCodesText} onChange={onChangeRecoveryCodesText}
                            placeholder={"Uno por línea:\nABCD-EFGH\nJKLM-NPQR\nSTUV-WXYZ"}
                            hint="Si lo dejás vacío, los códigos ya guardados no se modifican." />
                    </FormGrid>

                    {useRecoveryFileName && (
                        <div className="LStudio__app_vault_recovery_import_source">
                            <FaFileImport /> Importado desde <strong>{useRecoveryFileName}</strong>
                        </div>
                    )}

                    {useRecoveryImportError && (
                        <p className="LStudio__app_vault_recovery_import_error">{useRecoveryImportError}</p>
                    )}

                    {RECOVERY_CODES.length > 0 && (
                        <div className="LStudio__app_vault_recovery_create_preview">
                            {RECOVERY_CODES.slice(0, 12).map((code, index) => {
                                const IS_VISIBLE = !!useRecoveryPreviewVisible[index];
                                return (
                                    <div key={`${code}_${index}`} className="LStudio__app_vault_recovery_create_code">
                                        <span>Código {index + 1}</span>
                                        <code>{IS_VISIBLE ? code : maskRecoveryCode(code)}</code>
                                        <button type="button" onClick={() => onToggleRecoveryPreview(index)}
                                            aria-label={IS_VISIBLE ? "Ocultar código" : "Mostrar código"}>
                                            {IS_VISIBLE ? <FaEyeSlash /> : <FaEye />}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {IS_EDIT && RECOVERY_CODES.length > 0 && (
                        <div className="LStudio__app_vault_recovery_edit_replace">
                            <FieldSwitch id="personal_vault_recovery_replace" label="Conjunto actual"
                                text="Archivar los códigos disponibles actuales y reemplazarlos por estos"
                                isChecked={useRecoveryReplace}
                                onChange={(event) => setRecoveryReplace(event.target.checked)} />
                        </div>
                    )}
                </div>

                <div className="LStudio__app_vault_recovery_create_import">
                    <div className="LStudio__app_vault_recovery_create_head">
                        <div>
                            <strong>Archivos de recuperación o emergencia</strong>
                            <span>Certificados, key files, PDF de recuperación u otro respaldo asociado al acceso.</span>
                        </div>
                        {IS_EDIT && (entry?.files?.length ?? 0) > 0 && (
                            <span className="LStudio__app_vault_recovery_create_count">
                                {entry.files.length} guardado{entry.files.length === 1 ? "" : "s"}
                            </span>
                        )}
                    </div>
                    <FormGrid columns={2}>
                        <FieldSelect id="personal_vault_recovery_file_purpose" label="Tipo de respaldo"
                            value={useRecoveryPurpose}
                            onChange={(event) => setRecoveryPurpose(event.target.value)}
                            options={OPTIONS_PERSONAL_VAULT_FILE_PURPOSE} />
                        <FieldFile id="personal_vault_recovery_files" label="Seleccionar archivo"
                            files={useRecoveryFiles} onChange={setRecoveryFiles}
                            dropText="Elegí uno o más archivos desde tu computadora" />
                    </FormGrid>
                    <p className="LStudio__app_vault_inline_hint">
                        Los archivos seleccionados se suben recién al guardar el acceso. Si no seleccionás ninguno, los existentes no se modifican.
                    </p>
                </div>
            </FormSection>

            <FormSection title="Campos propios" columns={1}
                description="Agregá datos que no encajan arriba. Marcá Cifrar cuando el valor sea sensible.">
                <div className="LStudio__app_vault_extra_rows">
                    {useExtra.map((row, index) => (
                        <div key={index} className="LStudio__app_vault_extra_row">
                            <div className="LStudio__app_vault_extra_row_head">
                                <span>Campo personalizado {index + 1}</span>
                                <AppButton size="sm" variant="ghost" tone="danger" icon={<FaTrash />} isIconOnly
                                    aria-label="Quitar campo" onClick={() => onRemoveExtra(index)} />
                            </div>
                            <FieldInput id={`extra_label_${index}`} label="Nombre"
                                placeholder="Ej: Número de socio" value={row.label}
                                onChange={(event) => onChangeExtra(index, "label", event.target.value)} />
                            <FieldInput id={`extra_value_${index}`} label="Valor"
                                type={row.isSecret ? "password" : "text"} placeholder="Ej: personal-001" value={row.value}
                                onChange={(event) => onChangeExtra(index, "value", event.target.value)} />
                            <div className="LStudio__app_vault_extra_secret">
                                <FieldSwitch id={`extra_secret_${index}`} label="Secreto"
                                    text="Cifrar" isChecked={row.isSecret}
                                    onChange={(event) => onChangeExtra(index, "isSecret", event.target.checked)} />
                            </div>
                        </div>
                    ))}
                </div>

                <AppButton className="LStudio__app_extra_add_button" size="sm" variant="soft" tone="neutral" icon={<FaPlus />} onClick={onAddExtra}>
                    Agregar campo
                </AppButton>
            </FormSection>

            <FormSection title="Uso y notas" description="Información no sensible para recordar cómo utilizar este acceso.">
                <FormGrid columns={3}>
                    <FieldSelect {...FORM.field("commandLanguage")} label="Lenguaje del comando"
                        options={OPTIONS_CODE_LANGUAGE} />
                    <FieldCode {...FORM.field("command")} label="Comando de conexión"
                        language={FORM.values.commandLanguage} rows={4}
                        placeholder="Ej: ssh usuario@host" span={3} />
                    <FieldRichText {...FORM.fieldValue("notes", { emptyValue: "" })}
                        label="Notas y aclaraciones no sensibles" minHeight={200}
                        placeholder="Contexto, explicación, pasos generales. No pongas secretos acá: usá Notas de seguridad cifradas."
                        span={3} />
                </FormGrid>
            </FormSection>

        </AppModal>
    );
};
